/**
 * LexisNexis display settings owned by Risk Management (risk-mgmt-panel → LexisNexis FraudPoint).
 *
 *   lexisnexis.display.showVerificationRiskIndex  'true' | 'false' (default false) — the InstantID
 *       Verification Risk Index (0-50) is not a gate; the FraudPoint score is (Bill 2026-10-06:
 *       "not relevant to FSR users since the FP Scoring is the gating").
 *   lexisnexis.followUps.available  CSV of follow-up tools PCB can actually run: kba, smartlinx,
 *       phoneAddress, location. Default empty — PCB's DDP Schedule A (2026-05-12) licenses InstantID
 *       Consumer (+ Red Flags), FraudPoint and Emailage only, so no tool is suggested that an FSR
 *       cannot perform (Bill 2026-10-06).
 *   lexisnexis.fraudpoint.lowFloor / moderateFloor — the RM-owned cut-offs (procedure 750 / 500).
 */

export const SETTING_SHOW_RISK_INDEX = 'lexisnexis.display.showVerificationRiskIndex';
export const SETTING_FOLLOWUPS_AVAILABLE = 'lexisnexis.followUps.available';

export type LnFollowUpTool = 'kba' | 'smartlinx' | 'phoneAddress' | 'location';

type Settings = Record<string, unknown> | undefined;

export function showVerificationRiskIndex(settings: Settings): boolean {
  const v = settings?.[SETTING_SHOW_RISK_INDEX];
  return typeof v === 'string' && v.trim().toLowerCase() === 'true';
}

export function availableFollowUps(settings: Settings): ReadonlySet<LnFollowUpTool> {
  const v = settings?.[SETTING_FOLLOWUPS_AVAILABLE];
  if (typeof v !== 'string') return new Set();
  const known: ReadonlyArray<LnFollowUpTool> = ['kba', 'smartlinx', 'phoneAddress', 'location'];
  const listed = v.split(',').map((s) => s.trim().toLowerCase());
  return new Set(known.filter((k) => listed.includes(k.toLowerCase())));
}

/** Which follow-up tool a free-text LexisNexis suggestion refers to, if any. */
export function followUpToolOf(text: string): LnFollowUpTool | null {
  const t = text.toLowerCase();
  if (/q\s*&\s*a|\bkba\b|knowledge-based/.test(t)) return 'kba';
  if (/smartlinx|find-a-person/.test(t)) return 'smartlinx';
  if (/phone\s*\/\s*address|phone lookup|address lookup/.test(t)) return 'phoneAddress';
  if (/location lookup/.test(t)) return 'location';
  return null;
}

export function fraudPointFloors(settings: Settings): { low: number; moderate: number } {
  const n = (k: string, d: number): number => {
    const raw = settings?.[k];
    const v = typeof raw === 'string' ? Number.parseInt(raw, 10) : typeof raw === 'number' ? raw : NaN;
    return Number.isFinite(v) ? v : d;
  };
  const low = n('lexisnexis.fraudpoint.lowFloor', 750);
  const moderate = n('lexisnexis.fraudpoint.moderateFloor', 500);
  // Incoherent values fall back to the procedure, as the server does.
  return low > moderate ? { low, moderate } : { low: 750, moderate: 500 };
}

/**
 * InstantID verification-summary codes → which element LexisNexis matched. Labels from PCB's own
 * InstantID policy export (docs/LexisNexis API Information/Implementation Files/
 * a1r9yfwb_policy_instantid_2231691.json): NAP 0-12 / NAS 0-12, e.g. "NAP - 11 (Last Address and
 * Phone Matched)", "NAS - 12 (First Last Address and SSN Matched)".
 */
const NAP_ADDRESS = new Set([3, 5, 6, 8, 10, 11, 12]);
const NAP_PHONE = new Set([4, 6, 7, 9, 10, 11, 12]);
const NAS_ADDRESS = new Set([3, 5, 6, 8, 10, 11, 12]);

function code(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const m = String(v).match(/\d+/);
  return m ? Number.parseInt(m[0], 10) : null;
}

export function napNasMatches(nap: string | number | null | undefined, nas: string | number | null | undefined):
  { address: boolean | null; phone: boolean | null } {
  const p = code(nap);
  const s = code(nas);
  const address = p == null && s == null ? null : (p != null && NAP_ADDRESS.has(p)) || (s != null && NAS_ADDRESS.has(s));
  const phone = p == null ? null : NAP_PHONE.has(p);
  return { address, phone };
}
